/**
 * KTT Platform - Key Generator & Transaction Utilities
 * Matches reference: KEY TEST = f(Amount + Date + Secret Key Constant) mod 99999
 */

export const KTT_EMAIL = "bospnktt@bankopfsaipan.com";
export const KTT_CODE = "51 210619 BOSKTT G";
export const COMPANY = "Dwin Payment Gateway";
export const SECRET_KEY_CONSTANT = 210609; // derived from KTT code

/**
 * FX Rates — rates per 1 USD
 * Used to convert foreign currency amounts to USD for wallet debiting
 */
export const FX_RATES = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 150.25,
  CHF: 0.88,
  AUD: 1.55,
  CAD: 1.37,
  THB: 33.85,
  HKD: 7.82,
  SGD: 1.34,
  CNY: 7.24,
  INR: 83.50,
  KRW: 1340.00,
  MYR: 4.72,
  PHP: 56.50,
  IDR: 15850.00,
  TWD: 32.10,
  NZD: 1.68,
  SEK: 10.45,
  NOK: 10.80,
  DKK: 6.88,
  ZAR: 18.75,
  BRL: 5.05,
  MXN: 17.10,
  AED: 3.67,
  SAR: 3.75,
};

/**
 * Convert foreign currency amount to USD equivalent
 * @param {number} amount - Amount in foreign currency
 * @param {string} currency - ISO currency code
 * @returns {number} USD equivalent
 */
export function toUSD(amount, currency) {
  if (!currency || currency === 'USD') return amount;
  const rate = FX_RATES[currency];
  if (!rate) return amount; // unknown currency, pass through
  return amount / rate;
}

/**
 * Get the FX rate for a currency (per 1 USD)
 */
export function getFXRate(currency) {
  return FX_RATES[currency] || 1;
}

/**
 * Format amount with currency symbol
 */
export function formatFXAmount(amount, currency) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
}

/**
 * Generate transaction reference: DWIN/TYPE/YYMMDD/HHMM/SEQ
 */
export function generateTxnRef(type = "TRF") {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hhmm = String(now.getHours()).padStart(2, "0") + String(now.getMinutes()).padStart(2, "0");
  const seq = String(Math.floor(Math.random() * 9000) + 1000);
  return `DWIN/${type}/${yy}${mm}${dd}/${hhmm}/${seq}`;
}

/**
 * Compute KEY TEST = (Amount*100 + DateInt + SecretKey) mod 99999
 */
export function computeKeyTest(amount, date, secretKey = SECRET_KEY_CONSTANT) {
  const amtInt = Math.round(parseFloat(amount) * 100);
  const dateInt = parseInt(date.replace(/-/g, "").slice(2));
  const raw = (amtInt + dateInt + secretKey) % 99999;
  return String(raw).padStart(5, "0");
}

// =============================================
// PER-ACCOUNT KEY TEST MEMORY
// The FIRST KTT sent to a given receiving account locks in a Key Test
// number. Every later KTT to the SAME account reuses that exact key —
// this is how the receiving bank authenticates the sender on repeat wires.
// =============================================

const ACCOUNT_KEYS_STORAGE = 'ktt_account_keys';

function _acctId(accountNo) {
  return (accountNo || '').trim().toUpperCase().replace(/\s+/g, '');
}

/** Load the full { accountId: record } map of locked keys. */
export function loadAccountKeys() {
  try { return JSON.parse(localStorage.getItem(ACCOUNT_KEYS_STORAGE)) || {}; }
  catch { return {}; }
}

/** Return the locked Key Test for an account, or null if never used before. */
export function getStoredKeyTest(accountNo) {
  const id = _acctId(accountNo);
  if (!id) return null;
  const rec = loadAccountKeys()[id];
  return rec ? rec.keyTest : null;
}

/**
 * Resolve the Key Test to DISPLAY for the current form (non-mutating).
 * - locked:true  → account seen before, key is fixed (reused).
 * - locked:false → new account, key is a live preview of amount/date and
 *                  will be committed (locked) when the KTT is sent.
 */
export function resolveAccountKeyTest(accountNo, amount, valueDate) {
  const stored = getStoredKeyTest(accountNo);
  if (stored) {
    const rec = loadAccountKeys()[_acctId(accountNo)];
    return { keyTest: stored, locked: true, firstUsed: rec?.createdAt || null };
  }
  const preview = (amount && valueDate) ? computeKeyTest(amount, valueDate) : '-----';
  return { keyTest: preview, locked: false, firstUsed: null };
}

/**
 * Persist on confirmed send. If the account already has a key, that key is
 * reused and returned unchanged. Otherwise the computed key is stored as the
 * permanent key for that account. Returns the final Key Test to use.
 */
export function commitAccountKeyTest(accountNo, amount, valueDate) {
  const id = _acctId(accountNo);
  const keys = loadAccountKeys();
  if (id && keys[id]) return keys[id].keyTest; // reuse the locked key
  const computed = computeKeyTest(amount, valueDate);
  if (id) {
    keys[id] = {
      keyTest: computed,
      firstAmount: parseFloat(amount) || 0,
      firstDate: valueDate,
      createdAt: new Date().toISOString(),
    };
    try { localStorage.setItem(ACCOUNT_KEYS_STORAGE, JSON.stringify(keys)); } catch {}
  }
  return computed;
}

/**
 * Format date as DD Mon YYYY
 */
export function formatDate(d) {
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric"
  });
}

/**
 * Format currency as USD
 */
export function formatUSD(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2
  }).format(n || 0);
}

/**
 * Today as ISO string (YYYY-MM-DD)
 */
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Convert ISO date to SWIFT date (YYMMDD)
 */
export function swiftDate(iso) {
  return (iso || todayISO()).replace(/-/g, "").slice(2);
}

// =============================================
// ISO 20022 XML MESSAGE GENERATORS
// MT199 → pacs.009 (FI to FI Payment Status)
// MT103 → pacs.008 (Customer Credit Transfer)
// MT940 → camt.053 (Bank to Customer Statement)
// =============================================

function isoTimestamp() {
  return new Date().toISOString().replace(/\.\d+Z/, 'Z');
}

function msgId(ref) {
  return ref.replace(/\//g, '-');
}

/**
 * pacs.009 — Replaces MT199 (Free Format / Pre-advice)
 * FIToFIFinancialInstitutionCreditTransfer
 */
export function buildMT199({ txnRef, amount, currency, valueDate, beneficiary, accountNo, receiverBank, receiverBIC, receiverKTTCode, senderBIC, keyTest, narrative }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.009.001.10"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <FICdtTrf>
    <GrpHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
      <NbOfTxs>1</NbOfTxs>
      <SttlmInf>
        <SttlmMtd>INDA</SttlmMtd>
      </SttlmInf>
    </GrpHdr>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>${mid}</InstrId>
        <EndToEndId>${mid}</EndToEndId>
        <TxId>${mid}</TxId>
      </PmtId>
      <IntrBkSttlmAmt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</IntrBkSttlmAmt>
      <IntrBkSttlmDt>${valueDate}</IntrBkSttlmDt>
      <InstgAgt>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
          <Nm>${COMPANY}</Nm>
        </FinInstnId>
      </InstgAgt>
      <InstdAgt>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(receiverBank || "").toUpperCase()}</Nm>
        </FinInstnId>
      </InstdAgt>
      <Cdtr>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(receiverBank || "").toUpperCase()}</Nm>
        </FinInstnId>
      </Cdtr>
      <CdtrAcct>
        <Id><Othr><Id>${accountNo}</Id></Othr></Id>
      </CdtrAcct>
      <InstrForInstdAgt>
        <InstrInf>${narrative || "WE HEREBY CONFIRM READINESS TO INITIATE CASH TRANSFER SUBJECT TO STANDARD BANKING PROCEDURES AND VALUE DATING."}</InstrInf>
      </InstrForInstdAgt>
      <SplmtryData>
        <Envlp>
          <KTT>
            <SenderKTTCode>${KTT_CODE}</SenderKTTCode>
            <ReceiverKTTCode>${receiverKTTCode || "N/A"}</ReceiverKTTCode>
            <KTTEmail>${KTT_EMAIL}</KTTEmail>
            <KeyTestNumber>${keyTest}</KeyTestNumber>
          </KTT>
          <Beneficiary>
            <Nm>${(beneficiary || "").toUpperCase()}</Nm>
            <AcctNo>${accountNo}</AcctNo>
          </Beneficiary>
          <AuthSignatory>${COMPANY}</AuthSignatory>
        </Envlp>
      </SplmtryData>
    </CdtTrfTxInf>
  </FICdtTrf>
</Document>`;
}

/**
 * pacs.008 — Replaces MT103 (Customer Credit Transfer)
 * FIToFICustomerCreditTransfer
 */
export function buildMT103({ txnRef, amount, currency, valueDate, beneficiary, accountNo, receiverBank, senderBIC, receiverBIC, receiverKTTCode, keyTest, remittanceInfo }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.008.001.10"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <FIToFICstmrCdtTrf>
    <GrpHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
      <NbOfTxs>1</NbOfTxs>
      <TtlIntrBkSttlmAmt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</TtlIntrBkSttlmAmt>
      <IntrBkSttlmDt>${valueDate}</IntrBkSttlmDt>
      <SttlmInf>
        <SttlmMtd>INDA</SttlmMtd>
      </SttlmInf>
    </GrpHdr>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>${mid}</InstrId>
        <EndToEndId>${mid}</EndToEndId>
        <TxId>${mid}</TxId>
        <UETR>${crypto.randomUUID ? crypto.randomUUID() : mid}</UETR>
      </PmtId>
      <PmtTpInf>
        <SvcLvl><Cd>SDVA</Cd></SvcLvl>
      </PmtTpInf>
      <IntrBkSttlmAmt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</IntrBkSttlmAmt>
      <IntrBkSttlmDt>${valueDate}</IntrBkSttlmDt>
      <ChrgBr>SHAR</ChrgBr>
      <InstgAgt>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
        </FinInstnId>
      </InstgAgt>
      <InstdAgt>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
        </FinInstnId>
      </InstdAgt>
      <Dbtr>
        <Nm>${COMPANY}</Nm>
        <PstlAdr>
          <Ctry>MP</Ctry>
        </PstlAdr>
      </Dbtr>
      <DbtrAcct>
        <Id><Othr><Id>0110002130</Id></Othr></Id>
        <Ccy>${currency}</Ccy>
      </DbtrAcct>
      <DbtrAgt>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
        </FinInstnId>
      </DbtrAgt>
      <CdtrAgt>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(receiverBank || "").toUpperCase()}</Nm>
        </FinInstnId>
      </CdtrAgt>
      <Cdtr>
        <Nm>${(beneficiary || "").toUpperCase()}</Nm>
      </Cdtr>
      <CdtrAcct>
        <Id><Othr><Id>${accountNo}</Id></Othr></Id>
      </CdtrAcct>
      <RmtInf>
        <Ustrd>${remittanceInfo || txnRef}</Ustrd>
      </RmtInf>
      <SplmtryData>
        <Envlp>
          <KTT>
            <SenderKTTCode>${KTT_CODE}</SenderKTTCode>
            <ReceiverKTTCode>${receiverKTTCode || "N/A"}</ReceiverKTTCode>
            <KTTEmail>${KTT_EMAIL}</KTTEmail>
            <KeyTestNumber>${keyTest}</KeyTestNumber>
          </KTT>
        </Envlp>
      </SplmtryData>
    </CdtTrfTxInf>
  </FIToFICstmrCdtTrf>
</Document>`;
}

/**
 * camt.053 — Replaces MT940 (Bank-to-Customer Statement)
 * BankToCustomerStatement
 */
export function buildMT940({ txnRef, amount, currency, valueDate, accountNo, openBal, closeBal, txnType, narrative, receiverBIC, receiverKTTCode }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  const cdtDbt = txnType === "credit" ? "CRDT" : "DBIT";
  const openAmt = parseFloat(openBal || 0).toFixed(2);
  const closeAmt = parseFloat(closeBal || 0).toFixed(2);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:camt.053.001.11"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <BkToCstmrStmt>
    <GrpHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
    </GrpHdr>
    <Stmt>
      <Id>${mid}-STMT</Id>
      <ElctrncSeqNb>00001</ElctrncSeqNb>
      <CreDtTm>${ts}</CreDtTm>
      <Acct>
        <Id><Othr><Id>${accountNo}</Id></Othr></Id>
        <Ccy>${currency}</Ccy>
        <Ownr>
          <Nm>${COMPANY}</Nm>
        </Ownr>
        <Svcr>
          <FinInstnId>
            <BICFI>${receiverBIC || ""}</BICFI>
          </FinInstnId>
        </Svcr>
      </Acct>
      <Bal>
        <Tp><CdOrPrtry><Cd>OPBD</Cd></CdOrPrtry></Tp>
        <Amt Ccy="${currency}">${openAmt}</Amt>
        <CdtDbtInd>CRDT</CdtDbtInd>
        <Dt><Dt>${valueDate}</Dt></Dt>
      </Bal>
      <Bal>
        <Tp><CdOrPrtry><Cd>CLBD</Cd></CdOrPrtry></Tp>
        <Amt Ccy="${currency}">${closeAmt}</Amt>
        <CdtDbtInd>CRDT</CdtDbtInd>
        <Dt><Dt>${valueDate}</Dt></Dt>
      </Bal>
      <Ntry>
        <Amt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</Amt>
        <CdtDbtInd>${cdtDbt}</CdtDbtInd>
        <Sts>
          <Cd>BOOK</Cd>
        </Sts>
        <BookgDt><Dt>${valueDate}</Dt></BookgDt>
        <ValDt><Dt>${valueDate}</Dt></ValDt>
        <AcctSvcrRef>${mid}</AcctSvcrRef>
        <BkTxCd>
          <Domn>
            <Cd>PMNT</Cd>
            <Fmly><Cd>RCDT</Cd><SubFmlyCd>ESCT</SubFmlyCd></Fmly>
          </Domn>
        </BkTxCd>
        <NtryDtls>
          <TxDtls>
            <Refs>
              <EndToEndId>${mid}</EndToEndId>
              <TxId>${mid}</TxId>
            </Refs>
            <RmtInf>
              <Ustrd>${narrative || "FUNDS TRANSFER"}</Ustrd>
            </RmtInf>
          </TxDtls>
        </NtryDtls>
        <AddtlNtryInf>${narrative || "FUNDS TRANSFER"} REF:${txnRef}</AddtlNtryInf>
      </Ntry>
      <AddtlStmtInf>KTT SENDER: ${KTT_CODE} | KTT RECEIVER: ${receiverKTTCode || "N/A"}</AddtlStmtInf>
    </Stmt>
  </BkToCstmrStmt>
</Document>`;
}

/**
 * pacs.009 Cover — Replaces MT202 / MT202COV
 * FIToFI Financial Institution Credit Transfer (Bank Cover Payment)
 * Used for interbank cover payments and nostro funding.
 */
export function buildMT202({ txnRef, amount, currency, valueDate, beneficiary, accountNo, receiverBank, senderBIC, receiverBIC, receiverKTTCode, keyTest, narrative }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pacs.009.001.10"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <!-- MT202 / MT202COV — Bank Cover Payment (FI-to-FI) -->
  <FICdtTrf>
    <GrpHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
      <NbOfTxs>1</NbOfTxs>
      <SttlmInf>
        <SttlmMtd>INDA</SttlmMtd>
        <ClrSys>
          <Cd>DWIN</Cd>
        </ClrSys>
      </SttlmInf>
      <InstgAgt>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
          <Nm>${COMPANY}</Nm>
        </FinInstnId>
      </InstgAgt>
      <InstdAgt>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(receiverBank || "").toUpperCase() || "CORRESPONDENT BANK"}</Nm>
        </FinInstnId>
      </InstdAgt>
    </GrpHdr>
    <CdtTrfTxInf>
      <PmtId>
        <InstrId>${mid}-202</InstrId>
        <EndToEndId>${mid}</EndToEndId>
        <TxId>${mid}</TxId>
      </PmtId>
      <IntrBkSttlmAmt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</IntrBkSttlmAmt>
      <IntrBkSttlmDt>${valueDate}</IntrBkSttlmDt>
      <PmtTpInf>
        <SvcLvl><Cd>SWIFT</Cd></SvcLvl>
        <LclInstrm><Cd>COV</Cd></LclInstrm>
        <CtgyPurp><Cd>CORT</Cd></CtgyPurp>
      </PmtTpInf>
      <InstgAgt>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
        </FinInstnId>
      </InstgAgt>
      <InstdAgt>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
        </FinInstnId>
      </InstdAgt>
      <Cdtr>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(beneficiary || receiverBank || "").toUpperCase()}</Nm>
        </FinInstnId>
      </Cdtr>
      <CdtrAcct>
        <Id><Othr><Id>${accountNo}</Id></Othr></Id>
      </CdtrAcct>
      <InstrForInstdAgt>
        <InstrInf>${narrative || "COVER PAYMENT — INTERBANK SETTLEMENT"}</InstrInf>
      </InstrForInstdAgt>
      <SplmtryData>
        <Envlp>
          <KTT>
            <SenderKTTCode>${KTT_CODE}</SenderKTTCode>
            <ReceiverKTTCode>${receiverKTTCode || "N/A"}</ReceiverKTTCode>
            <KTTEmail>${KTT_EMAIL}</KTTEmail>
            <KeyTestNumber>${keyTest}</KeyTestNumber>
            <MsgCategory>MT202-COVER</MsgCategory>
          </KTT>
        </Envlp>
      </SplmtryData>
    </CdtTrfTxInf>
  </FICdtTrf>
</Document>`;
}

/**
 * camt.052 — Replaces MT950 (Statement Message / Balance Report)
 * BankToCustomerAccountReport — Intraday balance + transaction summary.
 * MT950 is a statement message showing opening/closing balance.
 */
export function buildMT950({ txnRef, amount, currency, valueDate, accountNo, openBal, closeBal, txnType, narrative, receiverBIC, receiverKTTCode }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  const cdtDbt = txnType === "credit" ? "CRDT" : "DBIT";
  const openAmt = parseFloat(openBal || 0).toFixed(2);
  const closeAmt = parseFloat(closeBal || 0).toFixed(2);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:camt.052.001.10"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <!-- MT950 — Statement Message / Balance Report -->
  <BkToCstmrAcctRpt>
    <GrpHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
      <MsgPgntn>
        <PgNb>1</PgNb>
        <LastPgInd>true</LastPgInd>
      </MsgPgntn>
    </GrpHdr>
    <Rpt>
      <Id>${mid}-RPT</Id>
      <ElctrncSeqNb>00001</ElctrncSeqNb>
      <RptgSeq>
        <FrToSeq>
          <FrSeq>00001</FrSeq>
          <ToSeq>00001</ToSeq>
        </FrToSeq>
      </RptgSeq>
      <CreDtTm>${ts}</CreDtTm>
      <FrToDt>
        <FrDtTm>${valueDate}T00:00:00Z</FrDtTm>
        <ToDtTm>${valueDate}T23:59:59Z</ToDtTm>
      </FrToDt>
      <Acct>
        <Id><Othr><Id>${accountNo}</Id></Othr></Id>
        <Ccy>${currency}</Ccy>
        <Ownr><Nm>${COMPANY}</Nm></Ownr>
        <Svcr>
          <FinInstnId>
            <BICFI>${receiverBIC || ""}</BICFI>
          </FinInstnId>
        </Svcr>
      </Acct>
      <Bal>
        <Tp><CdOrPrtry><Cd>OPBD</Cd></CdOrPrtry></Tp>
        <Amt Ccy="${currency}">${openAmt}</Amt>
        <CdtDbtInd>CRDT</CdtDbtInd>
        <Dt><Dt>${valueDate}</Dt></Dt>
      </Bal>
      <Bal>
        <Tp><CdOrPrtry><Cd>CLBD</Cd></CdOrPrtry></Tp>
        <Amt Ccy="${currency}">${closeAmt}</Amt>
        <CdtDbtInd>CRDT</CdtDbtInd>
        <Dt><Dt>${valueDate}</Dt></Dt>
      </Bal>
      <TxsSummry>
        <TtlNtries>
          <NbOfNtries>1</NbOfNtries>
          <TtlNetNtry>
            <Amt>${parseFloat(amount).toFixed(2)}</Amt>
            <CdtDbtInd>${cdtDbt}</CdtDbtInd>
          </TtlNetNtry>
        </TtlNtries>
      </TxsSummry>
      <Ntry>
        <Amt Ccy="${currency}">${parseFloat(amount).toFixed(2)}</Amt>
        <CdtDbtInd>${cdtDbt}</CdtDbtInd>
        <Sts><Cd>BOOK</Cd></Sts>
        <BookgDt><Dt>${valueDate}</Dt></BookgDt>
        <ValDt><Dt>${valueDate}</Dt></ValDt>
        <AcctSvcrRef>${mid}</AcctSvcrRef>
        <BkTxCd>
          <Domn>
            <Cd>PMNT</Cd>
            <Fmly><Cd>RCDT</Cd><SubFmlyCd>ESCT</SubFmlyCd></Fmly>
          </Domn>
        </BkTxCd>
        <NtryDtls>
          <TxDtls>
            <Refs>
              <EndToEndId>${mid}</EndToEndId>
              <TxId>${mid}</TxId>
            </Refs>
            <RmtInf>
              <Ustrd>${narrative || "STATEMENT ENTRY"} REF:${txnRef}</Ustrd>
            </RmtInf>
          </TxDtls>
        </NtryDtls>
        <AddtlNtryInf>${narrative || "STATEMENT ENTRY"}</AddtlNtryInf>
      </Ntry>
      <AddtlRptInf>KTT SENDER: ${KTT_CODE} | KTT RECEIVER: ${receiverKTTCode || "N/A"}</AddtlRptInf>
    </Rpt>
  </BkToCstmrAcctRpt>
</Document>`;
}

/**
 * camt.998 / Free Format — Replaces MT999
 * ProprietaryMessage — Free format bank-to-bank communication.
 * MT999 is a free text message with no structured fields.
 */
export function buildMT999({ txnRef, amount, currency, valueDate, beneficiary, accountNo, receiverBank, senderBIC, receiverBIC, receiverKTTCode, keyTest, narrative }) {
  const ts = isoTimestamp();
  const mid = msgId(txnRef);
  return `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:camt.998.001.03"
          xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <!-- MT999 — Free Format Message (Bank-to-Bank Communication) -->
  <PrtryMsg>
    <MsgHdr>
      <MsgId>${mid}</MsgId>
      <CreDtTm>${ts}</CreDtTm>
    </MsgHdr>
    <Rltd>
      <Ref>${mid}</Ref>
    </Rltd>
    <Prtry>
      <Tp>MT999</Tp>
      <Sndr>
        <FinInstnId>
          <BICFI>${senderBIC || ""}</BICFI>
          <Nm>${COMPANY}</Nm>
        </FinInstnId>
      </Sndr>
      <Rcvr>
        <FinInstnId>
          <BICFI>${receiverBIC || ""}</BICFI>
          <Nm>${(receiverBank || beneficiary || "").toUpperCase()}</Nm>
        </FinInstnId>
      </Rcvr>
      <ValDt>${valueDate}</ValDt>
      <Ccy>${currency}</Ccy>
      <Amt>${parseFloat(amount || 0).toFixed(2)}</Amt>
      <FreeText>${narrative || "FREE FORMAT MESSAGE — BANK TO BANK COMMUNICATION"}</FreeText>
      <Ref>${accountNo || "N/A"}</Ref>
      <KTTData>
        <SenderKTTCode>${KTT_CODE}</SenderKTTCode>
        <ReceiverKTTCode>${receiverKTTCode || "N/A"}</ReceiverKTTCode>
        <KTTEmail>${KTT_EMAIL}</KTTEmail>
        <KeyTestNumber>${keyTest}</KeyTestNumber>
      </KTTData>
    </Prtry>
  </PrtryMsg>
</Document>`;
}
